import { useAgentComplianceReviewKeyTermsQuery } from '@client/graphql/__generated__/main-operations';
import { KeyTermsSectionFragment } from '@client/graphql/__generated__/types';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSharedPageContext } from '../../../contexts/SharedPageContext';

interface UseAgentComplianceReviewKeyTermsResult {
  keyTerms: KeyTermsSectionFragment[];
  loading: boolean;
}

/**
 * Loads the agent compliance review key terms for the current share link.
 *
 * Mirrors {@link useAgentComplianceReviewOverview}'s token (`?token=` query
 * param) + folder (route param) + verification gating: the query is skipped
 * until {@link SharedPage} has verified the link and both the token and folder
 * are present.
 *
 * The main service forwards the key terms as an opaque JSON scalar (the
 * document service produces the fully-deserialized structure), so the result is
 * cast back to the typed {@link KeyTermsSectionFragment} shape the
 * {@link KeyTermsCard} consumes.
 */
export const useAgentComplianceReviewKeyTerms =
  (): UseAgentComplianceReviewKeyTermsResult => {
    const { ingestionFolderUuid } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? undefined;
    const { isShareLinkVerified } = useSharedPageContext();

    const { data, loading } = useAgentComplianceReviewKeyTermsQuery({
      variables: {
        input: {
          token: token ?? '',
          ingestionFolderUuid: ingestionFolderUuid ?? '',
        },
      },
      skip: !isShareLinkVerified || !token || !ingestionFolderUuid,
    });

    const keyTerms =
      (data?.agentComplianceReviewKeyTerms as
        | KeyTermsSectionFragment[]
        | undefined) ?? [];

    return { keyTerms, loading };
  };
